import { useState } from "react";
import { Link } from "react-router-dom";
import { Beaker, Thermometer, Wind, Droplet } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Titration from "@/components/lab/Titration";
import Calorimetry from "@/components/lab/Calorimetry";
import GasLawsLab from "@/components/lab/GasLawsLab";
import Seo from "@/components/Seo";

type LabId = "titration" | "calorimetry" | "gas-laws";

const LABS: { id: LabId; label: string; teks: string; blurb: string; icon: typeof Droplet }[] = [
  {
    id: "titration",
    label: "Titration",
    teks: "§112.35(c)(10)(I), (13)",
    blurb: "Strong acid–strong base. Drip the titrant, find the equivalence point, plot the curve.",
    icon: Droplet,
  },
  {
    id: "calorimetry",
    label: "Calorimetry",
    teks: "§112.35(c)(14)",
    blurb:
      "Drop a hot metal sample into water. Watch heat transfer to equilibrium and verify q_lost = q_gained.",
    icon: Thermometer,
  },
  {
    id: "gas-laws",
    label: "Gas Laws",
    teks: "§112.35(c)(11)",
    blurb: "Boyle, Charles, and full PV=nRT. Vary one variable, watch the others respond.",
    icon: Wind,
  },
];

export default function VirtualLabs() {
  const [active, setActive] = useState<LabId>("titration");

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Virtual Chemistry Labs — ElementPad" description="Guided virtual chemistry experiments with lab notebook export, perfect for distance learning and homework." path="/lab/virtual" />
      <AppHeader />
      <Main className="py-8">
        <div className="max-w-[1200px] mx-auto px-4 space-y-8">
          <header className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Link to="/lab" className="hover:text-foreground transition-colors">
                Online Lab
              </Link>
              <span>/</span>
              <span className="text-foreground">Virtual labs</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-primary/10 text-primary p-2">
                <Beaker size={20} aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  Virtual labs
                </h1>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Three quantitative simulations students can run from any device. Data
                  is computed live in the browser - nothing leaves the device.
                </p>
              </div>
            </div>
          </header>

          <Tabs value={active} onValueChange={(v) => setActive(v as LabId)}>
            <TabsList className="flex-wrap h-auto">
              {LABS.map((lab) => {
                const Icon = lab.icon;
                return (
                  <TabsTrigger key={lab.id} value={lab.id} className="gap-1.5">
                    <Icon size={14} aria-hidden="true" />
                    {lab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {LABS.map((lab) => (
              <TabsContent key={lab.id} value={lab.id} className="mt-4 space-y-4">
                <div className="rounded-md border border-border bg-muted/30 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground max-w-3xl">
                    {lab.blurb}
                  </p>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    TEKS {lab.teks}
                  </span>
                </div>
                {lab.id === "titration" && <Titration />}
                {lab.id === "calorimetry" && <Calorimetry />}
                {lab.id === "gas-laws" && <GasLawsLab />}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </Main>
    </div>
  );
}